import logoOnDark from "../assets/bytecode-dark.svg?raw";
import logoOnLight from "../assets/bytecode-light.svg?raw";
import type { SiteLink, SiteSettings } from "../types.ts";
import KitButton, { useLink } from "./KitButton.tsx";

function Logo({ svg, className }: { svg: string; className: string }) {
  // inline SVG: identical on server and client, no extra request
  return <span className={`cms-logo ${className}`} dangerouslySetInnerHTML={{ __html: svg }} />;
}

function NavLink({ link, className }: { link: SiteLink; className?: string }) {
  return (
    <a className={className} {...useLink(link.href)}>
      {link.label}
    </a>
  );
}

function HeaderCta({ link }: { link: SiteLink }) {
  const attrs = useLink(link.href);
  return (
    <KitButton {...attrs} href={attrs.href ?? "#"}>
      {link.label}
    </KitButton>
  );
}

export function SiteHeader({ site }: { site: SiteSettings }) {
  const { navLinks, cta } = site.header;
  return (
    <div className="cms-container cms-header__inner">
      <a href="/" aria-label={site.seo.siteName}>
        <Logo svg={logoOnLight} className="cms-header__logo" />
      </a>
      <nav className="cms-header__nav" aria-label="Main">
        {navLinks.map((link) => (
          <NavLink key={link.href} link={link} className="cms-header__link" />
        ))}
        {cta && <HeaderCta link={cta} />}
      </nav>
    </div>
  );
}

export function SiteFooter({ site, year }: { site: SiteSettings; year: number }) {
  const { tagline, email, linkGroups, legalLinks, copyright } = site.footer;
  return (
    <div className="cms-container">
      <div className="cms-footer__top">
        <div>
          <Logo svg={logoOnDark} className="cms-footer__logo" />
          {tagline && <p className="cms-footer__tagline">{tagline}</p>}
          {email && (
            <a className="cms-footer__email" href={`mailto:${email}`}>
              {email}
            </a>
          )}
        </div>
        {linkGroups.map((group) => (
          <div key={group.title}>
            <p className="cms-footer__title">{group.title}</p>
            <ul className="cms-footer__links">
              {group.links.map((link) => (
                <li key={link.href}>
                  <NavLink link={link} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="cms-footer__bottom">
        <p>
          © {year} {copyright}
        </p>
        {legalLinks.map((link) => (
          <NavLink key={link.href} link={link} />
        ))}
      </div>
    </div>
  );
}
